import {
  MONO_NUMERIC_STYLE,
  TABLE_SORTER_TOOLTIP,
  LIST_ICON_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../../../i18n/index.js';
import React, { type ReactElement, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Tooltip, Typography } from 'antd';
import { ArrowRightOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table/interface';
import { Table } from 'antd';

import { FilterBar, FilterSearch, FilterSelect } from '../../../shared/ui/FilterBar.js';
import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import type { HotLeadItem, HotLeadReason } from '../api/hot-leads.client.js';
import { useHotLeads } from '../hooks/useHotLeads.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { winProbabilityFactorText } from '../../../shared/format/win-probability-labels.js';

const { Text } = Typography;

type FilterValue = 'all' | HotLeadReason;
type StageFilterValue = 'all' | string;

type ChipTone = 'default' | 'success' | 'warning' | 'error' | 'processing';

/** Win-probability badge tone: strong / moderate / weak outlook. */
function probabilityTone(score: number): ChipTone {
  if (score >= 70) return 'success';
  if (score >= 40) return 'warning';
  return 'error';
}

/**
 * Stage → token tone. The old page used antd colour presets (blue / orange /
 * gold / green / red); the shared chip carries the semantic tones instead.
 * `getEnumLabel` localizes the label and leaves custom stages untouched.
 */
function stageTone(stage: string): ChipTone {
  const normalized = stage.toLowerCase();
  if (normalized === 'won') return 'success';
  if (normalized === 'lost') return 'error';
  return 'processing';
}

/** Reason → token tone (replaces the antd colour presets). */
const REASON_TONES: Record<HotLeadReason, ChipTone> = {
  overdue_next_activity: 'error',
  missing_next_activity: 'warning',
  stale_activity: 'default',
  overdue_task: 'error',
  due: 'processing',
  high_value: 'success',
};

const FILTER_OPTIONS: readonly FilterValue[] = [
  'all',
  'overdue_next_activity',
  'missing_next_activity',
  'stale_activity',
  'overdue_task',
  'due',
  'high_value',
];

/** URL parameter names — the filters are shareable links. */
const PARAM_FILTER = 'filter';
const PARAM_STAGE = 'stage';
const PARAM_QUERY = 'q';
/** Pagination param owned by `useDataViewPagination`. */
const PARAM_PAGE = 'page';

export function HotLeadsPage(): ReactElement {
  const { t, tc } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters live in the URL, not in component state: reloading or sharing the
  // link keeps the same slice of the queue.
  const rawFilter = searchParams.get(PARAM_FILTER);
  const filter: FilterValue =
    rawFilter !== null && (FILTER_OPTIONS as readonly string[]).includes(rawFilter)
      ? (rawFilter as FilterValue)
      : 'all';
  const stageFilter: StageFilterValue = searchParams.get(PARAM_STAGE) ?? 'all';
  const searchQuery = searchParams.get(PARAM_QUERY) ?? '';

  const query = useHotLeads(filter === 'all' ? {} : { filter });

  const setParam = (name: string, value: string | undefined): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === undefined || value === '' || value === 'all') next.delete(name);
        else next.set(name, value);
        // Changing a filter returns to the first page IN THE SAME update:
        // `resetPage()` builds its params from the render-time snapshot, so a
        // second setSearchParams call would clobber this one and drop the filter.
        next.delete(PARAM_PAGE);
        return next;
      },
      { replace: true },
    );
  };

  const rawLeads = query.data ?? [];

  const availableStages = useMemo(() => {
    const stages = new Set<string>();
    for (const lead of rawLeads) {
      stages.add(lead.deal.stage);
    }
    return Array.from(stages).sort((a, b) => a.localeCompare(b));
  }, [rawLeads]);

  const visibleLeads = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return rawLeads.filter((lead) => {
      if (stageFilter !== 'all' && lead.deal.stage !== stageFilter) return false;
      if (normalizedQuery.length === 0) return true;

      const haystack = [
        lead.deal.title,
        lead.deal.contactName ?? '',
        lead.deal.stage,
        t(`crm.hot_leads.reason.${lead.primaryReason}`),
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [rawLeads, searchQuery, stageFilter, t]);
  const pagination = useDataViewPagination(visibleLeads);

  const columns: ColumnsType<HotLeadItem> = [
    {
      title: t('crm.hot_leads.table_header.deal'),
      key: 'deal',
      render: (_: unknown, record) => (
        <Text strong style={{ fontSize: 13, color: 'var(--ant-color-text)' }}>
          {record.deal.title}
        </Text>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.contact'),
      dataIndex: ['deal', 'contactName'],
      key: 'contact',
      render: (name: string | null) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {name ?? <Text disabled>—</Text>}
        </Text>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.stage'),
      dataIndex: ['deal', 'stage'],
      key: 'stage',
      align: 'center',
      render: (stage: string) => (
        <EntityStatusChip tone={stageTone(stage)}>
          {getEnumLabel('dealStage', stage, t)}
        </EntityStatusChip>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.amount'),
      dataIndex: ['deal', 'amount'],
      key: 'amount',
      render: (amount: number | string, record) => (
        <Text
          style={{
            color: 'var(--ant-color-text)',
            ...MONO_NUMERIC_STYLE,
            fontWeight: 550,
          }}
        >
          {typeof amount === 'number'
            ? amount.toLocaleString(i18n.locale === 'en' ? 'en-US' : 'ru-RU')
            : amount}{' '}
          {record.deal.currency}
        </Text>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.reason'),
      dataIndex: 'primaryReason',
      key: 'reason',
      render: (reason: HotLeadReason) => (
        <EntityStatusChip tone={REASON_TONES[reason] ?? 'default'}>
          {t(`crm.hot_leads.reason.${reason}`)}
        </EntityStatusChip>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.score'),
      dataIndex: 'score',
      key: 'score',
      align: 'center',
      render: (score: number) => (
        <Text
          style={{
            color: 'var(--ant-color-primary)',
            ...MONO_NUMERIC_STYLE,
            fontWeight: 550,
          }}
        >
          {score}
        </Text>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.win_probability'),
      dataIndex: ['winProbability', 'score'],
      key: 'winProbability',
      align: 'center',
      // Client-side re-sort by probability; the server order keeps the
      // attention score primary with probability as the tie-breaker.
      sorter: (left, right) =>
        (left.winProbability?.score ?? 0) - (right.winProbability?.score ?? 0),
      render: (_: unknown, record) =>
        record.winProbability ? (
          <Tooltip
            title={
              <div>
                {record.winProbability.factors.map((factor) => (
                  <div key={`${factor.code}-${factor.impact}`}>
                    {winProbabilityFactorText({ t, tc }, factor)}
                  </div>
                ))}
              </div>
            }
          >
            <span data-testid={`hotleads-win-probability-${record.deal.id}`}>
              <EntityStatusChip tone={probabilityTone(record.winProbability.score)}>
                {record.winProbability.score}%
              </EntityStatusChip>
            </span>
          </Tooltip>
        ) : (
          <Text disabled>—</Text>
        ),
    },
    {
      title: t('crm.hot_leads.table_header.recommendation'),
      dataIndex: 'recommendation',
      key: 'recommendation',
      render: (recommendation: HotLeadItem['recommendation'] | undefined) => (
        <EntityStatusChip tone={recommendation === 'email' ? 'processing' : 'warning'}>
          {t(`crm.hot_leads.recommendation.${recommendation ?? 'call'}`)}
        </EntityStatusChip>
      ),
    },
    {
      title: t('crm.hot_leads.table_header.action'),
      key: 'action',
      align: 'center',
      render: (_: unknown, record) => (
        <Button
          data-testid={`hotleads-action-${record.deal.id}`}
          size="small"
          icon={<ArrowRightOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/deals/${record.deal.id}?tab=chat`);
          }}
        >
          {t('crm.hot_leads.open_recovery')}
        </Button>
      ),
    },
  ];

  // A stage kept in the URL may be absent from the current reason-filtered
  // slice; keep it in the options so the control still shows what is applied.
  const stageOptions = useMemo(() => {
    const stages = new Set(availableStages);
    if (stageFilter !== 'all') stages.add(stageFilter);
    return Array.from(stages);
  }, [availableStages, stageFilter]);

  return (
    <div className="animate-fade-in flex h-full flex-col">
      <PageHeader
        title={t('crm.hot_leads.recovery_title')}
        subtitle={
          <span data-testid="hotleads-visible-count">
            {t('crm.hot_leads.visible_count', {
              visible: visibleLeads.length,
              total: rawLeads.length,
            })}
          </span>
        }
        actions={
          <Tooltip title={t('crm.hot_leads.refresh')}>
            <Button
              aria-label={t('crm.hot_leads.refresh')}
              icon={<ReloadOutlined />}
              onClick={() => void query.refetch()}
              style={LIST_ICON_BUTTON_STYLE}
            />
          </Tooltip>
        }
        testId="page-header"
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FilterBar
          search={
            <FilterSearch
              id="hotleads-search"
              aria-label={t('crm.hot_leads.search_label')}
              placeholder={t('crm.hot_leads.search_placeholder')}
              value={searchQuery}
              onChange={(value) => {
                setParam(PARAM_QUERY, value);
              }}
            />
          }
        >
          <FilterSelect
            aria-label={t('crm.hot_leads.reason_filter')}
            value={filter}
            onChange={(value) => {
              setParam(PARAM_FILTER, value);
            }}
            options={FILTER_OPTIONS.map((item) => ({
              value: item,
              label: t(`crm.hot_leads.filter.${item}`),
            }))}
          />

          <FilterSelect
            aria-label={t('crm.hot_leads.stage_filter')}
            value={stageFilter}
            onChange={(value) => {
              setParam(PARAM_STAGE, value);
            }}
            options={[
              {
                value: 'all',
                label: t('crm.hot_leads.all_stages'),
              },
              ...stageOptions.map((stage) => ({
                value: stage,
                label: getEnumLabel('dealStage', stage, t),
              })),
            ]}
          />
        </FilterBar>

        <div className="min-h-0 flex-1" data-testid="hotleads-list-view" style={LIST_SURFACE_STYLE}>
          {query.isLoading ? (
            <QuietListSkeleton rows={5} />
          ) : query.isError ? (
            <div className="p-6">
              <ErrorState
                title={t('crm.hot_leads.load_error')}
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            </div>
          ) : (
            <Table<HotLeadItem>
              showSorterTooltip={TABLE_SORTER_TOOLTIP}
              dataSource={visibleLeads}
              columns={columns}
              rowKey={(r) => r.deal.id}
              pagination={pagination.pagination}
              size="small"
              data-testid="hotleads-table"
              onRow={(record) =>
                ({
                  'data-testid': `hotleads-row-${record.deal.id}`,
                  style: { cursor: 'pointer' },
                  onClick: () => navigate(`/deals/${record.deal.id}?tab=chat`),
                }) as React.HTMLAttributes<HTMLElement>
              }
              locale={{
                emptyText: <QuietEmpty text={t('crm.hot_leads.empty')} />,
              }}
              style={{ fontSize: 13 }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
